import { useState } from "react";
import { IconButton, Modal, Button as RsuiteButton } from "rsuite";
import TrashIcon from "@rsuite/icons/Trash";
import { DataTable, useEditableRow, EditableRowActions, EditableTableCell, FIELD_TYPES } from "../../../components";
import WinzRatesService from "../winzrates.service";
import ToastService from "../../../services/toast.service";
import "./winzRatesTable.css";

// Field configuration for the winz rates table
const FIELD_CONFIG = {
  rateCode: {
    type: FIELD_TYPES.NUMBER,
    placeholder: "Enter rate code",
    min: 0,
  },
  maxRate: {
    type: FIELD_TYPES.NUMBER,
    placeholder: "0.00",
    min: 0,
    step: 0.01,
    format: (val) => {
      const num = parseFloat(val);
      return isNaN(num) ? "0.00" : num.toFixed(2);
    }
  }
};

function WinzRatesTable({
  data,
  sortColumn,
  sortType,
  onSortColumn,
  loading,
  activeFilters,
  onFilterClick,
  // Pagination props
  currentPage,
  pageSize,
  totalItems,
  onPageChange,
  onPageSizeChange,
}) {
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [rateToDelete, setRateToDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);

  // Use the editable row hook
  const {
    editingRowId,
    editingData,
    saving,
    startEditing,
    cancelEditing,
    handleFieldChange,
    saveRow,
    isEditing
  } = useEditableRow({
    onSave: handleSaveWinzRate,
    onError: (error) => {
      ToastService.error(`Failed to update winz rate: ${error.message || "Unknown error"}`);
    }
  });

  async function handleSaveWinzRate(id, updatedData) {
    await WinzRatesService.updateWinzRateRecord(id, updatedData);
    ToastService.success("Winz rate updated successfully");
  }

  const handleDeleteWinzRate = (rate) => {
    setRateToDelete(rate);
    setDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!rateToDelete) return;

    setDeleting(true);
    try {
      await WinzRatesService.deleteWinzRate(rateToDelete.id);
      ToastService.success("Winz rate deleted successfully");
      setDeleteModalOpen(false);
      setRateToDelete(null);
    } catch (error) {
      ToastService.error(`Failed to delete winz rate: ${error.message || "Unknown error"}`);
    } finally {
      setDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleteModalOpen(false);
    setRateToDelete(null);
  };

  const columns = [
    {
      key: "actions",
      title: "Actions",
      width: 150,
      sortable: false,
      filterable: false,
      render: (rowData) => (
        <div className="action-buttons" style={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <EditableRowActions
            rowData={rowData}
            isEditing={isEditing(rowData.id)}
            onEdit={startEditing}
            onSave={saveRow}
            onCancel={cancelEditing}
            saving={saving}
          />
          <IconButton
            icon={<TrashIcon />}
            size="xs"
            appearance="subtle"
            onClick={() => handleDeleteWinzRate(rowData)}
            title="Delete Winz Rate"
            className="delete-button"
            style={{ display: isEditing(rowData.id) ? "none" : "block" }}
          />
        </div>
      ),
    },
    {
      key: "rateCode",
      dataKey: "rateCode",
      title: "Rate Code",
      width: 200,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="rateCode"
          fieldConfig={FIELD_CONFIG.rateCode}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
    {
      key: "maxRate",
      dataKey: "maxRate",
      title: "Max Rate",
      flexGrow: 1,
      sortable: true,
      filterable: true,
      render: (rowData) => (
        <EditableTableCell
          rowData={rowData}
          fieldKey="maxRate"
          fieldConfig={FIELD_CONFIG.maxRate}
          editingRowId={editingRowId}
          editingData={editingData}
          onFieldChange={handleFieldChange}
        />
      ),
    },
  ];

  return (
    <>
      <DataTable
        data={data}
        columns={columns}
        sortColumn={sortColumn}
        sortType={sortType}
        onSortColumn={onSortColumn}
        loading={loading}
        activeFilters={activeFilters}
        onFilterClick={onFilterClick}
        expandable={false}
        showRowCount={true}
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        onPageChange={onPageChange}
        onPageSizeChange={onPageSizeChange}
      />
      <Modal open={deleteModalOpen} onClose={cancelDelete} size="sm">
        <Modal.Header>
          <Modal.Title>Delete Winz Rate</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete the winz rate with code "{rateToDelete?.rateCode}"?
          <br />
          This action cannot be undone.
        </Modal.Body>
        <Modal.Footer>
          <RsuiteButton onClick={confirmDelete} appearance="primary" loading={deleting} disabled={deleting}>
            Delete
          </RsuiteButton>
          <RsuiteButton onClick={cancelDelete} appearance="subtle" disabled={deleting}>
            Cancel
          </RsuiteButton>
        </Modal.Footer>
      </Modal>
    </>
  );
}

export default WinzRatesTable;
